import { useState } from "react";
import { ButtonBase, Popover } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Menu, SearchField } from "../../primitive-components";
import colors from "../../config/colors";
import { ELEVATION } from "../../config/shadows";
import { colorPalette } from "../../config/colorPalette";

const { brand, mercury, mercuryAlpha } = colorPalette;

/**
 * BrandSwitcher
 *
 * The brand name with a caret; opens a search box and a Menu of the brands to switch to.
 *
 * Props:
 * - brands: [{ id, label, ... }]
 * - current: the brand shown, one of `brands` (or null)
 * - onChange: (brand) => void, called with the picked item of `brands`
 * - onOpen: () => void, called when the menu opens
 */
// Up to this many brands the list is short enough to scan, so there is no search box.
const SEARCH_FROM = 6;

export default function BrandSwitcher({ brands, current, onChange, onOpen }) {
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState("");
  const open = Boolean(anchor);
  const matches = brands.filter((brand) =>
    brand.label.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const close = () => {
    setAnchor(null);
    setQuery("");
  };
  const pick = (brand) => {
    close();
    if (brand.id !== current?.id) onChange(brand);
  };
  const Caret = open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  // With one brand there is nothing to switch to.
  const disabled = brands.length <= 1;

  return (
    <>
      <ButtonBase
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          setAnchor(event.currentTarget);
          onOpen?.();
        }}
        sx={{
          height: 44, // the height of the other inputs
          minWidth: 160,
          maxWidth: 240,
          padding: "0 11px",
          gap: "8px",
          justifyContent: "space-between",
          // Orange while open or focused, like the other fields.
          border: `1px solid ${open ? brand[700] : mercuryAlpha[8]}`,
          borderRadius: "8px",
          fontFamily: '"Lexend", sans-serif',
          fontSize: 14,
          lineHeight: 1.45,
          color: mercury[950],
          "&:hover": { backgroundColor: mercuryAlpha[5] },
          "&.Mui-focusVisible, &:focus-visible": { borderColor: brand[700] },
          "&.Mui-disabled": { color: mercuryAlpha[65] },
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {current?.label || "Select brand"}
        </span>
        <Caret sx={{ fontSize: 20, color: mercuryAlpha[65], flexShrink: 0 }} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: "4px",
              minWidth: 240,
              padding: "3px",
              borderRadius: "8px",
              backgroundColor: colors.white,
              border: `1px solid ${mercuryAlpha[8]}`,
              boxShadow: ELEVATION,
            },
          },
        }}
      >
        {brands.length >= SEARCH_FROM && (
          <SearchField
            autoFocus
            placeholder="Search brands"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              // Enter picks the first match.
              if (event.key === "Enter" && matches[0]) pick(matches[0]);
            }}
            sx={{ mb: "3px" }}
          />
        )}
        <Menu
          sx={{
            maxHeight: 280,
            overflowY: "auto",
            border: "none",
            boxShadow: "none",
            padding: 0,
          }}
          items={
            matches.length
              ? matches.map((brand) => ({
                  label: brand.label,
                  selected: brand.id === current?.id,
                  onClick: () => pick(brand),
                }))
              : [{ label: "No brands found", disabled: true }]
          }
        />
      </Popover>
    </>
  );
}
